import Dagre from '@dagrejs/dagre';
import { Node, Edge, useReactFlow } from '@xyflow/react';
import { DefaultNodeData } from './data';

export const calculateLayout = (
  nodes: Node<DefaultNodeData>[],
  edges: Edge[],
  options: { direction: string; ranksep?: number }
): { nodes: Node<DefaultNodeData>[]; edges: Edge[] } => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: options.direction, ranksep: options.ranksep, nodesep: 70 });
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  nodes.forEach((node) =>
    g.setNode(node.id, {
      ...node,
      width: node.measured?.width ?? 0,
      height: node.measured?.height ?? 0,
    })
  );

  // Disable optimal order heuristic to prevent nodes from being reordered
  Dagre.layout(g, { disableOptimalOrderHeuristic: true });

  // There was a strange bug that would occur when expanding the first child node (after the root start node)
  // that would cause the y position of the root start (index 0) and the first child (index 1) to change causing
  // a "jump" effect where the root start node would move down from its starting y position.
  // In order to prevent this, we set the y position of the first two nodes to fixed values.
  const getCustomYPosition = (node: Node<DefaultNodeData>) => {
    // NOTE: This is assuming the first node is the root start node so this will not work if the first node is not the root start node.
    const startNode = nodes[0];
    if (node.id === startNode.id) {
      return 0; // Fixed position for the root start node
    }

    // find out if this is the first child node of the start node
    const nodeSourceEdge = edges.find((edge) => edge.target === node.id);
    if (startNode && nodeSourceEdge && nodeSourceEdge.source === startNode.id) {
      return 100; // Fixed position for the first child node
    }

    // For all other nodes, use the calculated y position
    return g.node(node.id).y;
  };

  return {
    nodes: nodes.map((node) => {
      const position = g.node(node.id);

      return {
        ...node,
        position: {
          x: position.x - (node.measured?.width ?? 0) / 2,
          y: getCustomYPosition(node), // Use the custom function to determine the y position
        },
        type: node.type || 'basic', // Ensure type is always defined
      };
    }),
    edges: edges.map((edge) => {
      return { ...edge };
    }),
  };
};

export const useCalculateLayout = ({ shortVersion }: { shortVersion?: boolean }) => {
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow<Node<DefaultNodeData>>();

  return ({ direction }: { direction: 'TB' | 'LR' }) => {
    const layout = calculateLayout(getNodes(), getEdges(), { direction, ranksep: shortVersion ? 50 : 75 });

    setNodes([...layout.nodes]);
    setEdges([...layout.edges]);

    return layout;
  };
};
